import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { reviver } from '../hydration.js';

export const MAX_PAGE_SIZE = 50;

/**
 * Ceiling for the legacy endpoints that use 100 item pages
 */
export const LARGE_MAX_PAGE_SIZE = 100;

/**
 * @deprecated Page size used by callers that fetch a whole table in one request.
 * Only allowed on the tables that already rely on it; new endpoints must use {@link MAX_PAGE_SIZE}.
 */
export const DEPRECATED_MAX_ALL_PAGES_SIZE = 10_000;

//#region Paging

/**
 * Paging capped at `maxPageSize`. A larger `pageSize` is a 400 rather than a page the database has
 * to build. `pageSize` stays optional so each endpoint keeps its own default page size.
 */
function pagingSchemaWithMaxPageSize(maxPageSize: number) {
  return z.object({
    pageIndex: z.coerce.number().int().nonnegative().default(0),
    pageSize: z.coerce.number().int().min(1).max(maxPageSize).optional(),
  });
}

/** Paging for every endpoint without a documented reason to serve more than {@link MAX_PAGE_SIZE}. */
export const pagingSchema = pagingSchemaWithMaxPageSize(MAX_PAGE_SIZE);

export type PagingModifier = z.infer<typeof pagingSchema>;

export const largePagingSchema = pagingSchemaWithMaxPageSize(LARGE_MAX_PAGE_SIZE);

/**
 * @deprecated Using {@link DEPRECATED_MAX_ALL_PAGES_SIZE} is a security risk.
 */
export const DEPRECATED_pagingSchemaWithMaxPageSize =
  z.object({
    pageIndex: z.coerce.number().int().nonnegative().default(0),
    pageSize: z.coerce.number().int().min(1).max(DEPRECATED_MAX_ALL_PAGES_SIZE).default(MAX_PAGE_SIZE),
  });

/**
 * @deprecated Using {@link DEPRECATED_MAX_ALL_PAGES_SIZE} is a security risk.
 */
export type DEPRECATED_PagingModifierWithMaxPageSize = z.infer<typeof DEPRECATED_pagingSchemaWithMaxPageSize>;

/**
 * @deprecated Using {@link DEPRECATED_MAX_ALL_PAGES_SIZE} is a security risk.
 */
export const DEPRECATED_allPagesModifier = { pageIndex: 0, pageSize: DEPRECATED_MAX_ALL_PAGES_SIZE } satisfies DEPRECATED_PagingModifierWithMaxPageSize;

export const typeaheadPagesModifier = { pageIndex: 0, pageSize: 10 } satisfies PagingModifier;

//#endregion

//#region Filtering

export interface FilteringModifier<FilterSet extends object> {
  filters?: FilterSet;
}

export function filteringSchemaFrom<FilterSetZodType extends z.SomeZodObject>(
  filterSetSchema: FilterSetZodType,
): z.ZodObject<{ filters: z.ZodOptional<z.ZodPipeline<AnyJsonValueZodType, FilterSetZodType>> }> {
  return z.object({
    filters: anyJsonValueSchema.pipe(filterSetSchema).optional(),
  });
}

export const typeaheadFilterSetSchema = z.object({
  typeahead: z.string().optional(),
});

//#endregion

//#region Sorting

export const sortingOrderSchema = z.enum(['asc', 'desc']);

export type SortingOrder = z.infer<typeof sortingOrderSchema>;

export interface SortingMethod<Field extends string> {
  field: Field;
  order: SortingOrder;
}

export function sortingMethodFromSpecifier<Field extends string>(
  specifier: SortingSpecifier<Field>,
): SortingMethod<Field> {
  const [field, order] = specifier.split('-');
  return {
    field: field as Field,
    order: order as SortingOrder,
  };
}

export function sortingMethodsOf<Field extends string>(specifiers: SortingSpecifier<Field>[]): SortingMethod<Field>[] {
  return specifiers.map((specifier) => sortingMethodFromSpecifier<Field>(specifier));
}

export type SortingSpecifier<Field extends string> = `${Field}-${SortingOrder}`;

export function sortingSpecifierSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>> {
  return z.string().superRefine((input, ctx): input is SortingSpecifier<Fields[number]> => {
    const [field, sortingOrder, third] = input.split('-');
    if (!field || !sortingOrder || Boolean(third)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sorting specifier must follow the format `${fieldName}-${sortingOrder}`',
      });
      return z.NEVER;
    }

    const fieldResult = enumSchema.safeParse(field);
    if (!fieldResult.success) {
      for (const issue of fieldResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    const sortingOrderResult = sortingOrderSchema.safeParse(sortingOrder);
    if (!sortingOrderResult.success) {
      for (const issue of sortingOrderResult.error.issues) {
        ctx.addIssue(issue);
      }
    }

    return true;
  });
}

export interface SortingModifier<Field extends string> {
  sortBy?: SortingSpecifier<Field>[];
}

export function sortOrderSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodArray<z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>>> {
  return z.array(sortingSpecifierSchemaFrom(enumSchema));
}

export function sortingSchemaFrom<Fields extends [string, ...string[]]>(
  enumSchema: z.ZodEnum<Fields>,
): z.ZodObject<{
  sortBy: z.ZodOptional<
    z.ZodPipeline<AnyJsonValueZodType, z.ZodArray<z.ZodEffects<z.ZodString, SortingSpecifier<Fields[number]>>>>
  >;
}> {
  const sortOrderSchema = sortOrderSchemaFrom(enumSchema);

  return z.object({
    sortBy: anyJsonValueSchema.pipe(sortOrderSchema).optional(),
  });
}

//#endregion

//#region Utilities

const anyJsonValueSchema = z.string().transform((s, ctx) => {
  try {
    return JSON.parse(s, reviver);
  } catch (e: unknown) {
    if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: e.message,
      });
    }
    return z.NEVER;
  }
});
type AnyJsonValueZodType = typeof anyJsonValueSchema;

export type QueryStringPrimitive =
  | string
  | number
  | boolean
  | Temporal.PlainDate
  | Temporal.PlainTime
  | Temporal.Instant
  | Temporal.Duration;

export type QueryStringValueSerializable =
  | QueryStringPrimitive
  | Array<QueryStringPrimitive>
  | Record<string, QueryStringPrimitive>;

export function toQueryStringValue<T extends QueryStringValueSerializable>(x: T): string {
  if (typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') {
    return x.toString();
  }
  if (!(typeof x === 'object')) {
    throw new TypeError('Cannot serialize into query string value');
  }

  if (
    x instanceof Temporal.PlainDate ||
    x instanceof Temporal.PlainTime ||
    x instanceof Temporal.Instant ||
    x instanceof Temporal.Duration
  ) {
    return x.toString();
  }

  if (Array.isArray(x)) {
    return JSON.stringify(x);
  }

  return JSON.stringify(x);
}

//#endregion
